import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";

const field =
  "h-11 bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/40";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Both passwords must match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen nv-grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px] nv-rise">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-2.5">
            <Logo size={30} withWordmark className="text-lg" testid="auth-logo" />
          </div>
          <ThemeToggle />
        </div>

        {done ? (
          <div data-testid="reset-success" className="space-y-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">Password updated</h1>
            <p className="text-sm text-zinc-400">
              Your new password is active and every other session has been signed out.
            </p>
            <Button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="h-10 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
              Sign in
            </Button>
          </div>
        ) : !token ? (
          <div data-testid="reset-missing-token" className="space-y-4">
            <h1 className="text-3xl font-extrabold tracking-tight">Link incomplete</h1>
            <p className="text-sm text-zinc-400">This reset link is missing its token. Request a fresh one.</p>
            <Link to="/forgot-password" className="text-sm text-indigo-400 hover:text-indigo-300">
              Send a new link
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Choose a new password</h1>
            <p className="text-sm text-zinc-500 mt-2 mb-8">Minimum 8 characters. Make it a good one.</p>
            <form onSubmit={submit} className="space-y-3">
              <Input data-testid="reset-password-input" className={field} type="password" required minLength={8} placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Input data-testid="reset-confirm-input" className={field} type="password" required minLength={8} placeholder="Repeat new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              {error && (
                <p data-testid="reset-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <Button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>
            <p className="text-sm text-zinc-500 mt-6">
              Need a new link?{" "}
              <Link data-testid="reset-goto-forgot" to="/forgot-password" className="text-indigo-400 hover:text-indigo-300">
                Request one
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
